import React from 'react';
import { Box } from '@mui/material';
import { logError } from 'utils/logger';
import { Listing, useListing } from 'components/shared/TableListing';
import { Column, ListingForm } from 'components/shared/TableListing/types';
import {
  allFacts,
  CriteriaBodyCell,
  CriteriaHeadCell,
  getAvailableConditions,
} from 'modules/jsonRulesCriterias/businessCriteria';
import { ActionPanel } from './ActionPanel';
import { useTiers } from './useInternalTiers';
import { BottomActionPanel } from './BottomActionPanel';
import { initialColumns, initialCriterias, initialTableData, newTier } from './constants';
import { FormCriteriaPath, TierEntity, TierTableContext } from './types';
import {
  collectCreateAPIPayloadFromTiers,
  collectUpdateAPIPayloadFromTiers,
  getTiersWithInitialCriterias,
} from './lib';

interface Props {
  accountId: string;
}

export const InternalTiersContainer = ({ accountId }: Props) => {
  const {
    form,
    columns,
    tableData,
    handleAddRow,
    handleAddColumn,
    handleRemoveRow,
    handleResetColumns,
    handleRemoveColumn,
    handleUpdateColumnType,
  } = useListing<TierEntity, TierTableContext>({
    headerColumns: initialColumns,
    bodyFields: initialTableData,
  });

  const onAddCriteriaColumn = (type: string) => {
    handleAddColumn({
      header: 'Criteria',
      renderKey: type,
      columnType: type,
      renderHeadCell: ({ existingColumnsTypes, column, columnIndex }) => {
        const unregisterBodyFields = () => {
          const bodyFields = form.getValues('bodyFields');

          bodyFields.forEach((_, rowIndex) => {
            const resetPath = `bodyFields.${rowIndex}.criteria.${column.columnType}` as FormCriteriaPath;
            form.unregister(resetPath);
          });
        };

        const onChange = (nextValue: string) => {
          unregisterBodyFields();
          handleUpdateColumnType(columnIndex, nextValue);
        };

        const onRemove = () => {
          unregisterBodyFields();
          handleRemoveColumn(columnIndex);
        };

        return (
          <CriteriaHeadCell
            onChange={onChange}
            onRemove={onRemove}
            listOfFacts={allFacts}
            currentValue={column.columnType}
            addedCriteriaTypes={existingColumnsTypes}
          />
        );
      },
      renderCell: ({ context: { form }, column, rowIndex }) => {
        const name = `bodyFields.${rowIndex}.criteria.${column.columnType}` as FormCriteriaPath;

        return <CriteriaBodyCell control={form.control} ruleType={column.columnType} name={name} />;
      },
    } satisfies Column<TierEntity, TierTableContext>);
  };

  const onTiersReceived = (tiers: TierEntity[], tiersTypes: string[]) => {
    tiers.length ? setSavedTiers(tiers, tiersTypes) : setInitialTiers();
  };

  const setSavedTiers = (tiers: TierEntity[], tiersTypes: string[]) => {
    form.reset({ bodyFields: tiers });
    form.setValue('bodyFields', tiers);
    tiersTypes.forEach(onAddCriteriaColumn);
  };

  const setInitialTiers = () => {
    const tiers = getTiersWithInitialCriterias(initialCriterias, initialTableData);
    form.reset({ bodyFields: tiers });
    form.setValue('bodyFields', tiers);
    initialCriterias.forEach(onAddCriteriaColumn);
  };

  const onAddCondition = () => {
    const added = columns.map(({ columnType }) => columnType);
    const availableCriteriaType = getAvailableConditions(added as string[]);

    if (!availableCriteriaType) return;

    onAddCriteriaColumn(availableCriteriaType);
  };

  const {
    tierId,
    isUpdating,
    isTiersLoading,
    prescreenCriteria,
    handleCreateTiers,
    handleUpdateTiers,
    isCriteriaStatusUpdating,
    handleUpdateCriteriaStatus,
  } = useTiers(accountId, onTiersReceived);

  const onSubmit = async (formData: ListingForm<TierEntity, TierTableContext>) => {
    try {
      if (!tierId) {
        const apiPayload = collectCreateAPIPayloadFromTiers(accountId, formData);
        await handleCreateTiers(apiPayload);
      } else {
        const apiPayload = collectUpdateAPIPayloadFromTiers(accountId, tierId, formData);
        await handleUpdateTiers(apiPayload);
      }
    } catch (error) {
      logError(error);
    }
  };

  return (
    <Box sx={{ marginTop: '0.5rem' }}>
      <Listing<TierEntity, TierTableContext>
        columns={columns}
        tableData={tableData}
        loading={isTiersLoading}
        injectContext={{ form, loading: isTiersLoading, handleRemoveRow }}
        actionPanelTop={({ context }) => (
          <ActionPanel
            tierId={tierId}
            form={context.form}
            onSubmit={onSubmit}
            isUpdating={isUpdating}
            isLoading={isTiersLoading}
            criteria={prescreenCriteria}
            onAddCondition={onAddCondition}
            isUpdatingStatus={isCriteriaStatusUpdating}
            onChangeCriteriaStatus={async updateDTO => {
              await handleUpdateCriteriaStatus(updateDTO);
              form.reset();
              handleResetColumns();
            }}
          />
        )}
        actionPanelBottom={() => (
          <BottomActionPanel
            isUpdating={isUpdating}
            isLoading={isTiersLoading}
            handleAddRow={() => {
              handleAddRow(newTier);
            }}
          />
        )}
      />
    </Box>
  );
};
